/**
 * Utility functions for route classification and management
 *
 * Three explicit buckets:
 *
 *   - sessionless public: token/public portals + previews. No employee session
 *     needed; AuthContext may clear stale sb-* tokens; SessionRecovery skips
 *     recovery entirely.
 *
 *   - auth-entry: routes that decide where an authenticated or unauthenticated
 *     user should go next (`/` HomeRoute, `/auth/redirect` email-link handler).
 *     Session must be preserved through these; token-wipe here breaks email
 *     deep links.
 *
 *   - OAuth callback: `/auth/callback(/cookie)?` — Supabase writes fresh tokens
 *     in this flow; pre-clearing them is never correct.
 */

/**
 * Token-authenticated portals and unauthenticated previews.
 *
 * Officers visiting their own preview (`?officer=true`) are authenticated
 * users, not sessionless — that exemption is preserved so LO profile
 * previews don't accidentally wipe a logged-in officer's session.
 */
export const isLegacyRateQuotePublicRoute = (pathname: string): boolean => {
  return pathname === '/rate-quote' || pathname.startsWith('/rate-quote/');
};

export const isSessionlessPublicRoute = (pathname: string): boolean => {
  // Officer previews: authenticated LO viewing their own content is NOT sessionless.
  // (Relies on window.location.search — keep the check at the top.)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('officer') === 'true') {
      return false;
    }
  }

  const sessionlessPatterns = [
    /^\/p\/[^/]+$/,                    // /p/:slug - Public loan officer profiles
    /^\/pre-approval\/[a-zA-Z0-9-]+$/, // /pre-approval/:token - Borrower links
    /^\/templates\//,                  // Template previews
    /^\/secureuploads(\/.*)?$/,        // Secure document upload portal
    /^\/batcave$/,                     // Public secure document transfer
    /^\/conditions\/[a-f0-9]+$/        // /conditions/:token - Public conditions portal
  ];

  return (
    isLegacyRateQuotePublicRoute(pathname) ||
    sessionlessPatterns.some(pattern => pattern.test(pathname))
  );
};

/**
 * Embedded template preview (website-template gallery/modal iframes).
 *
 * These documents must never touch the shared auth session: with
 * persistSession enabled, the iframe's GoTrueClient recovers the parent's
 * localStorage session and re-broadcasts SIGNED_IN over BroadcastChannel into
 * the parent app, which can remount the page hosting the iframes (infinite
 * reload loop). Deliberately narrower than isSessionlessPublicRoute — the
 * ?officer=true exemption there must never re-enable session sharing inside
 * an iframe.
 */
export const isEmbeddedTemplatePreview = (
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : ''
): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  let embedded = false;
  try {
    embedded = window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws — that only happens when framed.
    embedded = true;
  }
  return embedded && /^\/templates\/(preview|demo)\//.test(pathname);
};

/**
 * Entry routes that dispatch authenticated/unauthenticated users.
 * Session init MUST run here; token-wipe MUST NOT.
 */
export const isAuthEntryRoute = (pathname: string): boolean => {
  return pathname === '/' || pathname === '/auth/redirect';
};

/**
 * OAuth callback paths. Supabase writes fresh tokens here — no pre-clearing.
 */
export const isOAuthCallbackRoute = (pathname: string): boolean => {
  return /^\/auth\/callback(\/.*)?$/.test(pathname);
};

export const isDevHarnessRoute = (pathname: string): boolean => {
  return Boolean(import.meta.env.DEV) && pathname === '/dev/investor-slideover-parity';
};

/**
 * Union helper: any auth-adjacent route that must be exempt from token-wipe
 * and recovery-triggered redirects.
 */
export const isAuthAdjacentRoute = (pathname: string): boolean => {
  return isAuthEntryRoute(pathname) || isOAuthCallbackRoute(pathname) || isDevHarnessRoute(pathname);
};
